/**
 * The API surface, one function per endpoint.
 *
 * Components never build URLs or bodies themselves — they call these. That keeps
 * the wire contract in one file, so an endpoint change is a one-line edit here
 * rather than a search across the component tree.
 */

import { api } from './client';
import { streamEvents, subscribeEvents } from './sse';

export { ApiError } from './client';
export { api };

/* -------------------------------------------------------------------- auth */
export const authApi = {
  /** Organizations are only needed when one username exists in several. */
  organizations: () => api.get('/auth/organizations'),
  login: (username, password, organization) =>
    api.post('/auth/login', { username, password, ...(organization ? { organization } : {}) }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/password', { current_password: currentPassword, new_password: newPassword }),
  /** Persists the interface theme on the signed-in user's record. */
  setTheme: (theme) => api.post('/auth/theme', { theme }),
  sessions: () => api.get('/auth/sessions'),
  revokeSessions: () => api.delete('/auth/sessions'),
};

/* ------------------------------------------------------------------- users */
export const usersApi = {
  list: (params = {}) => {
    const query = new URLSearchParams({ limit: 200, ...params });
    return api.get(`/users?${query}`);
  },
  get: (id) => api.get(`/users/${id}`),
  create: (payload) => api.post('/users', payload),
  update: (id, patch) => api.patch(`/users/${id}`, patch),
  /** Destructive: cascades to the user's conversations, jobs, prompts and images. */
  remove: (id) => api.delete(`/users/${id}?confirm=true`),
};

/* ----------------------------------------------------------- organizations */
export const organizationsApi = {
  list: () => api.get('/organizations'),
  get: (id) => api.get(`/organizations/${id}`),
  create: (payload) => api.post('/organizations', payload),
  update: (id, patch) => api.patch(`/organizations/${id}`, patch),

  listKeys: (organizationId) => api.get(`/organizations/${organizationId}/api-keys`),
  /** The response contains `key` — the only time the secret is ever returned. */
  createKey: (organizationId, payload) =>
    api.post(`/organizations/${organizationId}/api-keys`, payload),
  revokeKey: (keyId) => api.post(`/api-keys/${keyId}/revoke`),
  removeKey: (keyId) => api.delete(`/api-keys/${keyId}`),
};

/* ------------------------------------------------------------------ system */
export const systemApi = {
  health: () => api.get('/health'),
  ready: () => api.get('/ready'),
  status: () => api.get('/system'),
};

/* ---------------------------------------------------------------- versions */
export const versionsApi = {
  list: () => api.get('/versions'),
  get: (id) => api.get(`/versions/${id}`),
  /** HF: loads the version on the worker. vLLM: loads its base model, under the policy. */
  load: (id) => api.post(`/versions/${id}/load`),
  // HF: releaseBase drops the base model too, handing the VRAM back to other
  // processes; without it only the ~250 MB adapter is detached.
  // vLLM: versionId names the version whose base model is unloaded (drained first).
  unload: ({ releaseBase = false, versionId } = {}) =>
    api.post(
      '/versions/unload',
      versionId ? { version_id: versionId } : { release_base: releaseBase },
    ),
  lock: (id, reason = '') => api.post(`/versions/${id}/lock`, { reason }),
  unlock: (id) => api.delete(`/versions/${id}/lock`),
  patch: (id, patch) => api.patch(`/versions/${id}`, patch),
  importable: () => api.get('/versions/importable'),
  import: (payload) => api.post('/versions/import', payload),
  remove: (id, { files = false } = {}) => api.delete(`/versions/${id}?files=${files}`),
};

/* --------------------------------------------------------------- serving */
/**
 * vLLM model serving (INFERENCE_BACKEND=vllm). Reading is open to every signed-in
 * user; every write needs an administrator signed in interactively.
 */
export const servingApi = {
  /** Policy, loaded base models, GPU, capacity and admission. */
  status: () => api.get('/serving'),
  /** Any of { multi_model, max_models, gpu_budget, default_weights }. */
  updatePolicy: (patch) => api.put('/serving/policy', patch),
  load: (versionId) => api.post('/serving/load', { version_id: versionId }),
  /** Drains in-flight requests (up to drainS seconds), then stops the server. */
  unload: (serverKey, { drainS = 60 } = {}) =>
    api.delete(`/serving/servers/${encodeURIComponent(serverKey)}?drain_s=${drainS}`),
};

/* -------------------------------------------------------------- settings */
/**
 * Deployment-wide settings. Reading is open to every signed-in user; every write
 * needs an administrator signed in interactively.
 */
export const settingsApi = {
  /** { mode: 'off'|'allowlist'|'any', allowed_hosts, limits, updated_by, updated_at, source } */
  imageUrls: () => api.get('/settings/image-urls'),
  /** Any of { mode, allowed_hosts }. */
  updateImageUrls: (patch) => api.put('/settings/image-urls', patch),

  /**
   * Settings → General: every limit and default — its .env value, the deployment
   * value, the organization's override and the value in force. `organizationId`
   * (superadmin) reads another organization's overrides.
   */
  runtime: ({ organizationId } = {}) =>
    api.get(
      organizationId
        ? `/settings/runtime?organization_id=${encodeURIComponent(organizationId)}`
        : '/settings/runtime',
    ),
  /** Superadmin. `values` maps setting keys to new values; null resets to .env. */
  updateRuntime: (values) => api.put('/settings/runtime', { values }),
  /** That organization's admin, or a superadmin. null removes the override. */
  updateOrganizationRuntime: (organizationId, values) =>
    api.put(`/settings/runtime/organizations/${organizationId}`, { values }),
};

/** Labels for the weights modes, as the API names them. */
export const WEIGHT_MODES = [
  {
    value: 'nf4_faithful',
    label: 'NF4-faithful',
    hint: 'Base weights quantised and restored exactly as in QLoRA training. Closest to how the adapter learned; full bf16 memory.',
  },
  {
    value: 'bf16',
    label: 'As published (bf16)',
    hint: 'The base checkpoint unmodified. Fastest to prepare; the numerics differ slightly from training.',
  },
  {
    value: 'bnb_4bit',
    label: '4-bit (bitsandbytes)',
    hint: 'Quantised when vLLM loads it. Least GPU memory, slowest kernels.',
  },
];

/* ------------------------------------------------------------ attributes */
export const attributesApi = {
  listSets: () => api.get('/attribute-sets'),
  getSet: (id, { grouped = true } = {}) => api.get(`/attribute-sets/${id}?grouped=${grouped}`),
};

/* ---------------------------------------------------------------- images */
export const imagesApi = {
  upload: (files) => {
    const form = new FormData();
    Array.from(files).forEach((file) => form.append('files', file));
    return api.post('/images', form);
  },
  get: (id) => api.get(`/images/${id}`),
  fileUrl: (id, { thumb = false } = {}) => api.url(`/images/${id}/file?thumb=${thumb}`),
  modelViewUrl: (id, versionId) =>
    api.url(`/images/${id}/model-view${versionId ? `?version_id=${versionId}` : ''}`),
};

/* ------------------------------------------------------------------ chat */
export const chatApi = {
  send: (payload, { signal } = {}) => api.post('/chat', payload, { signal }),
  stream: (payload, handlers) => streamEvents('/chat/stream', payload, handlers),
  listConversations: (params = {}) => {
    const query = new URLSearchParams({ limit: 50, offset: 0, ...params });
    return api.get(`/conversations?${query}`);
  },
  getConversation: (id) => api.get(`/conversations/${id}`),
  rename: (id, title) => api.patch(`/conversations/${id}`, { title }),
  setPinned: (id, pinned) => api.post(`/conversations/${id}/pin`, { pinned }),
  remove: (id) => api.delete(`/conversations/${id}`),
  attributes: (id) => api.get(`/conversations/${id}/attributes`),
};

/* --------------------------------------------------------------- predict */
export const predictApi = {
  run: (payload) => api.post('/predict', payload),
};

/* ----------------------------------------------------------------- batch */
export const batchApi = {
  create: (payload) => api.post('/batch/jobs', payload),
  list: (params = {}) => {
    const query = new URLSearchParams({ limit: 50, offset: 0, ...params });
    return api.get(`/batch/jobs?${query}`);
  },
  get: (id, { items = false } = {}) => api.get(`/batch/jobs/${id}?items=${items}`),
  summary: (id) => api.get(`/batch/jobs/${id}/summary`),
  start: (id) => api.post(`/batch/jobs/${id}/start`),
  setPinned: (id, pinned) => api.post(`/batch/jobs/${id}/pin`, { pinned }),
  cancel: (id) => api.post(`/batch/jobs/${id}/cancel`),
  remove: (id) => api.delete(`/batch/jobs/${id}`),
  subscribe: (id, handlers) => subscribeEvents(`/batch/jobs/${id}/stream`, handlers),
  downloadUrl: (id, format = 'csv') => api.url(`/batch/jobs/${id}/results?format=${format}`),
  download: (id, format = 'csv') => api.download(`/batch/jobs/${id}/results?format=${format}`),
};

/* --------------------------------------------------------------- compare */
export const compareApi = {
  run: (payload, { signal } = {}) => api.post('/compare', payload, { signal }),
};

/* ---------------------------------------------------------------- search */
export const searchApi = {
  /**
   * Always answers 200, even when Milvus is down — it reports the problem rather
   * than being one. Read it before rendering a query box, so nobody types into a
   * control that cannot answer.
   */
  status: () => api.get('/search/status'),

  /**
   * Find visually similar survey images. Send exactly one of `image_id` (others
   * like this one) or `query` (a short description). The API rejects both or
   * neither with a 422 rather than picking one.
   *
   * Keep `query` to a phrase: the image model's text tower caps at 64 tokens and
   * silently truncates the rest.
   */
  images: (payload, { signal } = {}) => api.post('/search/images', payload, { signal }),

  /** Search everything the model has said — chat, batch and /predict alike. */
  analyses: (payload, { signal } = {}) => api.post('/search/analyses', payload, { signal }),

  /** Retrieval over coding guidance you have ingested. Empty until you do. */
  manual: (payload, { signal } = {}) => api.post('/search/manual', payload, { signal }),

  ingestManual: (chunks) => api.post('/search/manual/ingest', { chunks }),
};

/* --------------------------------------------------------------- prompts */
export const promptsApi = {
  list: () => api.get('/prompts'),
  get: (id) => api.get(`/prompts/${id}`),
  create: (payload) => api.post('/prompts', payload),
  update: (id, patch) => api.patch(`/prompts/${id}`, patch),
  remove: (id) => api.delete(`/prompts/${id}`),
  render: (payload) => api.post('/prompts/render', payload),
  exportUrl: () => api.url('/prompts/export'),
  import: (prompts, { overwrite = false } = {}) =>
    api.post('/prompts/import', { prompts, overwrite }),
};
